import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, Typography,
  useMediaQuery, useTheme, IconButton, Collapse, Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getCountryName } from '../../../utils/countryUtils';

// Компонент для отображения строки таблицы с возможностью разворачивания на мобильных устройствах
const VisitRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Форматируем дату и время
  const formattedDate = format(new Date(row.createdAt), 'dd.MM.yyyy HH:mm:ss', { locale: ru });
  
  if (isMobile) {
    return (
      <React.Fragment>
        <TableRow hover>
          <TableCell sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                  {formattedDate}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                  {row.path.length > 20 ? `${row.path.substring(0, 20)}...` : row.path}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingTop: 0, paddingBottom: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem' }}>
                  Детальная информация
                </Typography>
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Страница:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                    {row.path}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Страна/Город:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {row.country === 'DEV' ? 'Локальная среда (Разработка)' : 
                      row.country ? getCountryName(row.country) : 'Неизвестно'}
                  </Typography>
                  {row.city && row.country !== 'DEV' && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      {row.city}
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Устройство:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {row.deviceType || 'Неизвестно'}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Браузер/ОС:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {row.browser ? `${row.browser}${row.os ? ` / ${row.os}` : ''}` : 'Неизвестно'}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Пользователь:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {row.user ? row.user.name : 'Гость'}
                    </Typography>
                    {row.isAdmin && (
                      <Chip size="small" label="Админ" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Время на странице:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {row.timeOnPage ? `${row.timeOnPage} сек.` : 'Н/Д'}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  
  // Для десктопа возвращаем обычную строку таблицы
  return (
    <TableRow hover>
      <TableCell>
        {formattedDate}
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {row.path}
        </Typography>
      </TableCell>
      <TableCell>
        {row.country ? (
          <Box>
            {row.country === 'DEV' ? (
              <>
                <Typography variant="body2" color="warning.main">Локальная среда</Typography>
                <Typography variant="caption" color="text.secondary">
                  Разработка
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{getCountryName(row.country)}</Typography>
                {row.city && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {row.city}
                  </Typography>
                )}
              </>
            )}
          </Box>
        ) : (
          'Неизвестно'
        )}
      </TableCell>
      <TableCell>
        {row.deviceType || 'Неизвестно'}
      </TableCell>
      <TableCell>
        <Box>
          {row.browser && (
            <Typography variant="body2">{row.browser}</Typography>
          )}
          {row.os && (
            <Typography variant="caption" color="text.secondary">
              {row.os}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {row.user ? (
          <Box>
            <Typography variant="body2">{row.user.name}</Typography>
            <Chip 
              size="small" 
              label={row.isAdmin ? 'Админ' : 'Пользователь'} 
              color={row.isAdmin ? 'primary' : 'default'} 
            />
          </Box>
        ) : (
          'Гость'
        )}
      </TableCell>
      <TableCell>
        {row.timeOnPage ? `${row.timeOnPage} сек.` : 'Н/Д'}
      </TableCell>
    </TableRow>
  );
};

// Компонент для отображения детальной таблицы посещений
const StatisticsTable = ({ 
  data, 
  page, 
  limit, 
  totalItems, 
  onPageChange, 
  onLimitChange 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Нет данных для отображения
        </Typography>
      </Box>
    );
  }

  // Обработчик изменения страницы
  const handleChangePage = (event, newPage) => {
    onPageChange(newPage);
  };

  // Обработчик изменения количества записей на странице
  const handleChangeRowsPerPage = (event) => {
    onLimitChange(parseInt(event.target.value, 10));
  };

  return (
    <Paper sx={{ 
      width: '100%', 
      overflow: 'hidden',
      boxShadow: { xs: 1, sm: 2 },
      borderRadius: { xs: 1, sm: 2 }
    }}>
      <TableContainer sx={{ 
        maxHeight: { xs: 'none', sm: 440 },
        overflowX: 'auto'
      }}>
        {isMobile ? (
          // Мобильная версия таблицы
          <Table aria-label="статистика посещений">
            <TableBody>
              {data.map((row) => (
                <VisitRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        ) : (
          // Десктопная версия таблицы
          <Table stickyHeader aria-label="статистика посещений">
            <TableHead>
              <TableRow>
                <TableCell>Дата и время</TableCell>
                <TableCell>Страница</TableCell>
                <TableCell>Страна/Город</TableCell>
                <TableCell>Устройство</TableCell>
                <TableCell>Браузер/ОС</TableCell>
                <TableCell>Пользователь</TableCell>
                <TableCell>Время на странице</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <VisitRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={isMobile ? [5, 10, 25] : [10, 25, 50, 100]}
        component="div"
        count={totalItems}
        rowsPerPage={limit}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={isMobile ? "Записей:" : "Записей на странице:"}
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} из ${count !== -1 ? count : `${to}+`}`
        }
        sx={{
          '.MuiTablePagination-selectLabel': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          },
          '.MuiTablePagination-select': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          },
          '.MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          },
          '.MuiTablePagination-actions': {
            marginLeft: { xs: 1, sm: 2 }
          }
        }}
      />
    </Paper>
  );
};

export default StatisticsTable;
